import Step from '@components/Wizard/Step'
import Steps from '@components/Wizard/Steps'

class Wizard 

  @wizard: (wizardOptions = {}) -> 
    components: { Steps, Step }
    data: ->
      activeStep:
        name: null
        idx: null
        node: null
      steps: []
    computed: 
      activeStepVm: -> @activeStep?.node?.$children[0]
      canGoBackward: ->
        return @activeStep.idx - 1 >= 0
      canGoForward: ->
        return @activeStep.idx + 1 < @steps.length
      validationErrors: -> @activeStep?.validationErrors || {}
      validationFormErrors: -> @validationErrors?.formErrors || {}

      wizard: -> @
    methods: 
      close: (options = {}) -> @saveStep(options) unless options.skipSave
      next: (options = {}) -> 
        nextStepIndex = @activeStep.idx + 1
        nextStepIndex = @steps.length - 1 if nextStepIndex >= @steps.length
        @navToStepIndex(nextStepIndex, options)
      back: (options = {}) ->
        nextStepIndex = @activeStep.idx - 1
        nextStepIndex = 0 if nextStepIndex < 0
        @navToStepIndex(nextStepIndex, options)
      stepForIndex: (idx) -> 
        step = @steps.find (s) -> s.idx is idx
        step ||= @activeStep
        return step
      stepForName: (name) ->  
        step = @steps.find (s) -> s.name is name
        step ||= @activeStep
        return step
      navToStepIndex: (idx, options = {}) -> 
        step = @stepForIndex(idx)
        step ||= @steps[0]
        return if @activeStep.idx is idx
        unless options.skipSave
          options.navToStepAfterSave = step
          @saveStep(options) 
        else 
          @activeStep = step
      navToStepName: (name, options = {}) -> 
        step = @stepForName(name)
        step ||= @steps[0]
        @navToStepIndex(step.idx, options)
      formIsValid: (stepIdx = null) ->
        step = @stepForIndex(stepIdx)
        0 is Object.keys(step.validationErrors?.formErrors || {}).length
      formIsInvalid: (stepIdx = null) -> return !@formIsValid(stepIdx)
      modelIsValid: (stepIdx = null) ->
        step = @stepForIndex(stepIdx)
        0 is Object.keys(step.validationErrors?.modelErrors || {}).length
      modelIsInvalid: (stepIdx = null) -> return !@modelIsValid(stepIdx) 
      stepIsValid: (stepIdx = null) ->
        return false if @formIsInvalid(stepIdx) || @modelIsInvalid(stepIdx)
        step = @stepForIndex(stepIdx)
        0 is Object.keys(step.validationErrors?.stepErrors || {}).length
      stepIsInvalid: (stepIdx = null) -> !@stepIsValid(stepIdx)

      stepComponent: (stepIdx = null) ->
        step = @stepForIndex(stepIdx)
        @stepComponentForName(step.name)
      stepComponentForName: (name) -> @$refs.steps.$children.find((s) -> s.name is name)?.$children?[0]

      validateActiveStep: -> @validateStep()

      validateStep: (stepIdx = null) ->
        @clearValidationErrors(stepIdx)
        step = @stepComponent(stepIdx)
        !!step?.validateStep?()

      setFormError: (field, error, stepIdx = null) -> 
        step = @stepForIndex(stepIdx)
        step.validationErrors.formErrors[field] = error
        @syncValidationErrors(stepIdx) # force reactivity
      setModelError: (field, error, stepIdx = null) -> 
        step = @stepForIndex(stepIdx)
        step.validationErrors.modelErrors[field] = error
        @syncValidationErrors(stepIdx) # force reactivity
      setStepError: (field, error, stepIdx = null) -> 
        step = @stepForIndex(stepIdx)
        step.validationErrors.stepErrors[field] = error
        @syncValidationErrors(stepIdx) # force reactivity

      clearValidationErrors: (stepIdx=null)->
        step = @stepForIndex(stepIdx)
        step.validationErrors = { formErrors: {}, modelErrors: {}, stepErrors: {} }

      clearError: (fields) ->
        fields = [fields] unless Array.isArray fields
        for field in fields
          @clearFormError(field)
          @clearModelError(field)
          @clearStepError(field)

      clearFormError: (field) ->
        delete @activeStep?.validationErrors.formErrors[field]
        @syncValidationErrors()
      clearFormErrors: (field) -> @step.validationErrors.formErrors = {}

      clearModelError: (field) ->
        delete @activeStep?.validationErrors.modelErrors[field]
        @syncValidationErrors()
      clearModelErrors: (field) -> @step.validationErrors.modelErrors = {}

      clearStepError: (field) ->
        delete @activeStep?.validationErrors.stepErrors[field]
        @syncValidationErrors()
      clearStepErrors: (field) -> @step.validationErrors.stepErrors = {}

      syncValidationErrors: (stepIdx=null)->
        step = @stepForIndex(stepIdx)
        step.validationErrors = Object.assign({}, step.validationErrors)

      saveStep: (options = {}) ->
        new Promise (resolve, reject) => 
          try 
            await @activeStepVm.beforeValidate?()
            await @activeStepVm.validateStep?() if !options.skipValidation
            await @activeStepVm.beforeSave?()
            saveFields = @activeStepVm.saveFields?()
            saveFields ||= {}
            for resourceName, fields of saveFields
              payload = @payloadForResourceFields resourceName, fields
              await @wizard.saveItemJob(resourceName, payload) if Object.keys(payload).length
            await @activeStepVm.afterSave?() 
            @activeStep = options.navToStepAfterSave if options.navToStepAfterSave?
            resolve()
          catch err 
            @wizard.setModelError('field', 'error')
            reject()
      payloadForResourceFields: (resourceName, fields) -> 
        fields = [fields] unless Array.isArray fields
        obj = @prime[resourceName]
        payload = {}
        payload.id = obj.id if obj.id?
        payload[field] = obj[field] for field in fields 
        payload
      calculateSteps: -> 
        return [] unless @$refs.steps?.$children
        nodes = (@$refs.steps.$children.filter (n) -> n.$options._componentTag is 'step')
        idx = 0
        @steps = nodes.map (s) -> 
          { 
            name: s.$options.propsData.name, idx: idx++, node : s, validationErrors: { formErrors: {}, modelErrors: {}, stepErrors: {} }
          } 
      startingStep: ->
        step = @stepForName wizardOptions.startStep if wizardOptions.startStep?
        step ||= @stepForName @$route.params.stepName if @$route.params.stepName?
        step ||= @stepForIndex @$route.params.stepIndex if @$route.params.stepIndex?
        step
      setInitialStep: -> 
        @activeStep = @startingStep()
        @activeStep = @stepForName @$route.query.step if @$route.query?.step?
        @activeStep = @steps[0] if @steps.length and not @activeStep?.name?
        @activeStep ||= { idx: -100, name: 'empty', node: null }

      initializeWizard: ->
        @calculateSteps()
        @setInitialStep()

    mounted: ->
      @initializeWizard() unless @activeStep?.name
    updated: ->
      @initializeWizard() unless @activeStep?.name

  @step: -> 
    props: ['wizard']
    methods: 
      back: -> @wizard.back()
      next: -> @wizard.next()
      canGoForward: -> @wizard.stepIsValid()
      saveStep: (options = { skipNav: false }) -> @wizard.saveStep(options)
      canGoBackward: -> return true
      beforeSave: -> 
      saveField: (resourceName, field) -> 
        resource = @wizard.prime[resourceName]
        return console.log 'no resource found' unless resource?
        payload = {}
        payload.id = resource.id
        payload[field] = resource[field]
        @saveStep(payload)

export default Wizard